import { proxy } from 'valtio';
import { EnrolmentSettingsDataTypes } from '../components/CourseReferralStepForms/types';
import { WorkSchedule, CourseSegment } from '@duke-hq/shared-constants/enrolments';
import type { CourseKind } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import type { CourseStatus } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { djs } from '@duke-hq/djs';
import { unknownOrDecimalToNumber } from './QuickReferralState';
import { Decimal } from 'decimal.js';

export enum CourseReferralModalMode {
  CREATE_NEW = 'CREATE_NEW',
  ADD_SECOND_SEMESTER = 'ADD_SECOND_SEMESTER',
  CREATE_NEW_FOR_SELECTED_COURSE = 'CREATE_NEW_FOR_SELECTED_COURSE',
}

export const courseReferralModalState = proxy({
  mode: CourseReferralModalMode.CREATE_NEW,
  selectedCourseAttributes: {
    id: '',
    identifier: '',
    price_aud: 0,
    specialisation: '',
    title: '',
    kind: '',
    status: undefined as CourseStatus | undefined,
  },
  enrolmentSettingsData: {
    contract_category: '',
    course_segment: CourseSegment.FULL_COURSE,
    enrolment_contract_type: '',
    external_id: '',
    semesterTwoExternalId: '',
    selected_consultant: { consultant_id: '', consultant_name: '' } as
      | {
          consultant_id: string;
          consultant_name: string;
        }
      | undefined,
    selected_site: { site_id: '', site_name: '', site_state: '' } as
      | {
          site_id: string;
          site_name: string;
          site_state: string | undefined;
        }
      | undefined,
    start_at_date: djs().format('YYYY-MM-DD'),
    semester_2_start_at_date: null as string | null,
    work_schedule: WorkSchedule.FULL_TIME,
    studentId: undefined as string | undefined,
    enrolmentId: undefined as string | undefined,
  },
  isModalOpen: false,
  selectedStudent: undefined as
    | {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        usi?: string;
      }
    | undefined,
  setSite: ({
    site_id,
    site_name,
    site_state,
  }: {
    site_id: string;
    site_name: string;
    site_state: string;
  }) => {
    courseReferralModalState.enrolmentSettingsData.selected_site = {
      site_id,
      site_name,
      site_state,
    };
  },
  setConsultant: ({
    consultant_id,
    consultant_name,
  }: {
    consultant_id: string;
    consultant_name: string;
  }) => {
    courseReferralModalState.enrolmentSettingsData.selected_consultant = {
      consultant_id,
      consultant_name,
    };
  },
  setStudent: ({
    id,
    firstName,
    lastName,
    email,
    usi,
  }: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    usi?: string;
  }) => {
    courseReferralModalState.selectedStudent = {
      id,
      firstName,
      lastName,
      email,
      usi,
    };
  },
  setSelectedCourseAttributes: ({
    id,
    identifier,
    price_aud,
    specialisation,
    title,
    kind,
    status,
  }: {
    id: string;
    identifier: string;
    price_aud: number | Decimal;
    specialisation: string;
    title: string;
    kind?: CourseKind;
    status?: CourseStatus;
  }) => {
    courseReferralModalState.selectedCourseAttributes = {
      id,
      identifier,
      price_aud: unknownOrDecimalToNumber(price_aud),
      specialisation,
      title,
      kind: kind ?? '',
      status: status ?? undefined,
    };
  },
  setEnrolmentSettingsData: ({
    contract_category,
    course_segment,
    enrolment_contract_type,
    external_id,
    semesterTwoExternalId,
    selected_site,
    selected_consultant,
    start_at_date,
    semester_2_start_at_date,
    work_schedule,
    studentId,
    enrolmentId,
  }: EnrolmentSettingsDataTypes) => {
    courseReferralModalState.enrolmentSettingsData = {
      contract_category,
      course_segment,
      enrolment_contract_type,
      external_id,
      semesterTwoExternalId,
      selected_site,
      selected_consultant,
      start_at_date,
      semester_2_start_at_date,
      work_schedule,
      studentId,
      enrolmentId,
    };
  },
  openModal: () => {
    courseReferralModalState.resetStates();
    courseReferralModalState.isModalOpen = true;
  },
  closeModal: () => {
    courseReferralModalState.isModalOpen = false;
    courseReferralModalState.resetStates();
  },
  setMode: (mode: CourseReferralModalMode) => {
    courseReferralModalState.mode = mode;
  },
  resetStates: () => {
    courseReferralModalState.selectedCourseAttributes = {
      id: '',
      identifier: '',
      title: '',
      specialisation: '',
      price_aud: 0,
      kind: '',
      status: undefined,
    };
    courseReferralModalState.enrolmentSettingsData = {
      contract_category: '',
      course_segment: CourseSegment.FULL_COURSE,
      enrolment_contract_type: '',
      external_id: '',
      semesterTwoExternalId: '',
      selected_site: { site_id: '', site_name: '', site_state: '' },
      selected_consultant: { consultant_id: '', consultant_name: '' },
      start_at_date: djs().format('YYYY-MM-DD'),
      semester_2_start_at_date: null,
      work_schedule: WorkSchedule.FULL_TIME,
      studentId: '',
      enrolmentId: '',
    };
    courseReferralModalState.selectedStudent = undefined;
    courseReferralModalState.isModalOpen = false;
    courseReferralModalState.mode = CourseReferralModalMode.CREATE_NEW;
  },
});
